<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  installment: { type: Object, default: null },
  responsibilities: { type: Array, default: () => [] },
});

const { t } = useI18n();
const cents = value => Number(value?.amount_cents || 0);
const currency = computed(
  () =>
    props.responsibilities[0]?.valor_pagamento?.currency ||
    props.installment?.parcela?.valor_parcela?.currency ||
    'BRL'
);
const formatCents = value =>
  new Intl.NumberFormat('pt-BR', {
    currency: currency.value,
    style: 'currency',
  }).format(value / 100);
const totalFor = field =>
  props.responsibilities.reduce(
    (total, responsibility) => total + cents(responsibility.pagamento?.[field]),
    0
  );
const paidTotal = computed(() =>
  props.responsibilities.reduce((total, responsibility) => {
    if (responsibility.comfirmacao_pagamento !== 1) return total;

    return (
      total +
      cents(responsibility.pagamento?.valor || responsibility.valor_pagamento)
    );
  }, 0)
);
const interestTotal = computed(() => totalFor('juros'));
const penaltyTotal = computed(() => totalFor('multa'));
const roleCount = role => {
  const rows = props.responsibilities.filter(
    responsibility => responsibility.papel === role
  );
  const confirmed = rows.filter(
    responsibility => responsibility.comfirmacao_pagamento === 1
  ).length;
  const label =
    role === 'Vendedor'
      ? t('REAL_ESTATE_FINANCE.DETAIL.SELLERS')
      : t('REAL_ESTATE_FINANCE.DETAIL.BUYERS');

  return `${confirmed}/${rows.length} ${label}`;
};
</script>

<template>
  <section
    data-testid="finance-payment-summary"
    class="rounded-lais border border-lais-border-soft bg-white p-5 shadow-lais-card"
  >
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div
          v-if="penaltyTotal || interestTotal"
          class="mb-3 flex flex-wrap gap-2"
        >
          <span
            v-if="penaltyTotal"
            class="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800"
          >
            {{
              t('REAL_ESTATE_FINANCE.DETAIL.PENALTY_VALUE', {
                value: formatCents(penaltyTotal),
              })
            }}
          </span>
          <span
            v-if="interestTotal"
            class="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800"
          >
            {{
              t('REAL_ESTATE_FINANCE.DETAIL.INTEREST_VALUE', {
                value: formatCents(interestTotal),
              })
            }}
          </span>
        </div>
        <p
          class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
        >
          {{ t('REAL_ESTATE_FINANCE.DETAIL.ALREADY_PAID') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2 text-xs font-bold">
        <span class="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
          {{ roleCount('Vendedor') }}
        </span>
        <span class="rounded-full bg-blue-100 px-3 py-1 text-blue-800">
          {{ roleCount('Comprador') }}
        </span>
      </div>
    </div>
    <p class="mt-4 font-redHatDisplay text-3xl font-bold text-lais-success">
      {{ formatCents(paidTotal) }}
    </p>
  </section>
</template>
